import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { Users, Clock, BarChart3, MousePointerClick } from "lucide-react";
import api from "../api/client";
import BehaviorNav from "./BehaviorNav";

const RANGES = ["7d", "30d", "90d"];

const tabs = [
  { to: "/behavior", label: "Overview" },
  { to: "/behavior/pages", label: "Pages" },
  { to: "/behavior/sessions", label: "Sessions" },
  { to: "/behavior/exit-intent", label: "Exit Intent" },
  { to: "/behavior/triggers", label: "Triggers" },
];

function StatCard({ icon: Icon, label, value, color = "text-copper" }) {
  return (
    <div className="bg-dark-300 border border-dark-50 rounded-xl p-5">
      <div className="flex items-center gap-3 mb-3">
        <div className="p-2 bg-dark-50/50 rounded-lg">
          <Icon size={18} className={color} />
        </div>
        <span className="text-sm text-gray-400">{label}</span>
      </div>
      <div className="text-2xl font-mono font-bold">{value ?? "—"}</div>
    </div>
  );
}

export default function BehaviorOverview({ selectedSite }) {
  const [data, setData] = useState(null);
  const [range, setRange] = useState("7d");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!selectedSite) return;
    setLoading(true);
    api
      .get("/behavior/overview/", {
        params: { website_id: selectedSite.id, range },
      })
      .then((res) => setData(res.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [selectedSite, range]);

  if (!selectedSite) {
    return (
      <div className="text-gray-500 text-center py-20">
        Select a website to view behavior analytics
      </div>
    );
  }

  const stats = data?.stats || {};
  const daily = (data?.daily || []).map((d) => ({
    date: new Date(d.date).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    }),
    visitors: d.visitors,
    pageviews: d.pageviews,
  }));
  const devices = data?.devices || [];
  const topPages = data?.top_pages || [];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">Behavior Analytics</h2>
        <div className="flex bg-dark-300 border border-dark-50 rounded-lg p-0.5">
          {RANGES.map((r) => (
            <button
              key={r}
              onClick={() => setRange(r)}
              className={`px-3 py-1.5 text-xs rounded-md transition-colors ${
                range === r
                  ? "bg-copper/20 text-copper"
                  : "text-gray-400 hover:text-white"
              }`}
            >
              {r}
            </button>
          ))}
        </div>
      </div>

      <BehaviorNav />

      {loading ? (
        <div className="animate-pulse space-y-4">
          <div className="grid grid-cols-4 gap-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-28 bg-dark-300 rounded-xl" />
            ))}
          </div>
          <div className="h-64 bg-dark-300 rounded-xl" />
        </div>
      ) : (
        <>
          {/* Stat cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard
              icon={Users}
              label="Visitors"
              value={stats.visitors?.toLocaleString()}
            />
            <StatCard
              icon={MousePointerClick}
              label="Sessions"
              value={stats.sessions?.toLocaleString()}
              color="text-blue-400"
            />
            <StatCard
              icon={Clock}
              label="Time (median · mean)"
              value={(stats.median_time || stats.avg_time) ? `${stats.median_time ?? 0}s · ${stats.avg_time ?? 0}s` : undefined}
              color="text-green-400"
            />
            <StatCard
              icon={BarChart3}
              label="Bounce Rate"
              value={
                stats.bounce_rate != null
                  ? `${stats.bounce_rate.toFixed(1)}%`
                  : undefined
              }
              color="text-amber-400"
            />
          </div>

          {/* Daily visitors/pageviews chart */}
          {daily.length > 1 && (
            <div className="bg-dark-300 border border-dark-50 rounded-xl p-5">
              <h3 className="text-sm font-semibold text-gray-300 mb-4">
                Daily Visitors & Pageviews
              </h3>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={daily}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                    <XAxis
                      dataKey="date"
                      tick={{ fill: "#64748b", fontSize: 11 }}
                      axisLine={{ stroke: "#1e293b" }}
                    />
                    <YAxis
                      tick={{ fill: "#64748b", fontSize: 11 }}
                      axisLine={{ stroke: "#1e293b" }}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: "#111827",
                        border: "1px solid #1e293b",
                        borderRadius: "8px",
                        fontSize: "12px",
                      }}
                    />
                    <Area
                      type="monotone"
                      dataKey="visitors"
                      stroke="#b87333"
                      fill="#b87333"
                      fillOpacity={0.15}
                      strokeWidth={2}
                    />
                    <Area
                      type="monotone"
                      dataKey="pageviews"
                      stroke="#60a5fa"
                      fill="#60a5fa"
                      fillOpacity={0.1}
                      strokeWidth={2}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {/* Device breakdown */}
          {devices.length > 0 && (
            <div className="bg-dark-300 border border-dark-50 rounded-xl p-5">
              <h3 className="text-sm font-semibold text-gray-300 mb-4">
                Device Breakdown
              </h3>
              <div className="h-48">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={devices} layout="vertical">
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke="#1e293b"
                      horizontal={false}
                    />
                    <XAxis
                      type="number"
                      tick={{ fill: "#64748b", fontSize: 11 }}
                      axisLine={{ stroke: "#1e293b" }}
                    />
                    <YAxis
                      type="category"
                      dataKey="device"
                      tick={{ fill: "#64748b", fontSize: 11 }}
                      axisLine={{ stroke: "#1e293b" }}
                      width={80}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: "#111827",
                        border: "1px solid #1e293b",
                        borderRadius: "8px",
                        fontSize: "12px",
                      }}
                    />
                    <Bar dataKey="count" fill="#b87333" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {/* Top Pages table */}
          <div className="bg-dark-300 border border-dark-50 rounded-xl overflow-hidden">
            <div className="px-5 py-3 border-b border-dark-50">
              <h3 className="text-sm font-semibold text-gray-300">
                Top Pages
              </h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs text-gray-500 border-b border-dark-50">
                    <th className="text-left py-2.5 px-4 font-medium">Path</th>
                    <th className="text-right py-2.5 px-3 font-medium">
                      Views
                    </th>
                    <th className="text-right py-2.5 px-3 font-medium">
                      Visitors
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {topPages.map((p, i) => (
                    <tr
                      key={i}
                      className="border-b border-dark-50/50 hover:bg-dark-50/30"
                    >
                      <td className="py-2.5 px-4">
                        <Link
                          to={`/behavior/page/${encodeURIComponent(p.path)}`}
                          className="font-mono text-xs text-copper hover:underline"
                        >
                          {p.path}
                        </Link>
                      </td>
                      <td className="py-2.5 px-3 text-right font-mono text-gray-400">
                        {p.pageviews?.toLocaleString()}
                      </td>
                      <td className="py-2.5 px-3 text-right font-mono text-gray-400">
                        {p.visitors?.toLocaleString()}
                      </td>
                    </tr>
                  ))}
                  {!topPages.length && (
                    <tr>
                      <td
                        colSpan={3}
                        className="py-12 text-center text-gray-500"
                      >
                        No page data yet
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
